<!--
  @description FilePond-powered file upload field with direct-to-Firebase Storage upload.
  Use for image uploads in admin forms (challenge images, pack covers) and for user profile
  images. Emits the Firebase Storage download URL once the upload completes, so the parent
  can immediately persist the URL to Firestore without handling the upload itself.

  Storage path routing is id-driven: when `id` equals `'user-image-uploader'` the file is
  written to `profileImages/{userId}/` under the authenticated user's uid. All other ids
  write to the shared `Images/` bucket. Pass `allowMultiple` and `maxFiles` to control
  upload concurrency.

  All FilePond UI labels are currently hardcoded in French. Callers cannot override them via
  props — a future refactor should source these from vue-i18n.

  @example
  <FileUpload
    id="pack-cover"
    title="Cover image"
    :validation="{ required: true }"
    :status="coverStatus"
    @update:modelValue="(url) => { form.coverUrl = url }"
  />

  @accessibility The underlying FilePond widget manages its own ARIA attributes and keyboard
  interactions. Ensure the wrapping `<label>` is not hidden from assistive technologies when
  `title` is provided — it is rendered as a standard visible label above the drop zone.
-->
<template>
  <div :id="id" class="file-upload">
    <label v-if="title !== undefined" :for="title" class="file-upload__label"
      >{{ title }}<span v-if="!validation.required"> ({{ $t('global.optional') }})</span></label
    >
    <file-pond
      ref="pond"
      :server="myServer"
      :allow-multiple="allowMultiple"
      :max-files="maxFiles"
      :files="files"
      labelIdle='Glisser et déposer un fichier ou <span class="filepond--label-action">Parcourir</span>'
      labelInvalidField="Le champ contient des fichiers invalides"
      labelFileWaitingForSize="En attente de la taille du fichier"
      labelFileSizeNotAvailable="Taille non disponible"
      labelFileLoading="Chargement"
      labelFileLoadError="Erreur lors du chargement"
      labelFileProcessing="Téléversement"
      labelFileProcessingComplete="Téléversement terminé"
      labelFileProcessingAborted="Téléversement annulé"
      labelFileProcessingError="Erreur lors du téléversement"
      labelFileProcessingRevertError="Erreur lors de la restauration"
      labelFileRemoveError="Erreur lors de la suppression"
      labelTapToCancel="cliquer pour annuler"
      labelTapToRetry="cliquer pour réessayer"
      labelTapToUndo="cliquer pour annuler"
      labelButtonRemoveItem="Supprimer"
      labelButtonAbortItemLoad="Annuler"
      labelButtonRetryItemLoad="Réessayer"
      labelButtonAbortItemProcessing="Annuler"
      labelButtonUndoItemProcessing="Revenie en arrière"
      labelButtonRetryItemProcessing="Réessayer"
      labelButtonProcessItem="Téléverser"
    />
    <p v-if="!status.valid" class="file-upload__error">
      {{ status.message }}
    </p>
  </div>
</template>

<script setup lang="ts">
import vueFilePond from 'vue-filepond'
import FilePondPluginImagePreview from 'filepond-plugin-image-preview'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css'
import 'filepond/dist/filepond.min.css'

import type { Status, ValidationParams } from '@/types/Types'
import { useFirebaseStorage } from '@/composables/useFirebaseStorage'

import { computed } from 'vue'

const props = defineProps({
  /**
   * Unique HTML id applied to the wrapper `<div>` and used to determine the Firebase Storage
   * path. Pass `'user-image-uploader'` to route uploads to `profileImages/{userId}/`;
   * any other value routes to the shared `Images/` bucket.
   */
  id: {
    type: String,
    required: true
  },
  /** Label text rendered above the drop zone. Omit to hide the label. */
  title: {
    type: String,
    required: false,
    default: undefined
  },
  /** When true, FilePond accepts more than one file at a time. Defaults to single-file mode. */
  allowMultiple: {
    type: Boolean,
    required: false,
    default: false
  },
  /** Maximum number of files FilePond will accept. Pass `null` (default) for no limit. */
  maxFiles: {
    type: Number,
    required: false,
    default: null
  },
  /** Pre-populated file list forwarded directly to FilePond's `files` prop. */
  files: {
    type: Array,
    required: false,
    default: () => []
  },
  /**
   * Externally managed validation status. Unlike other form components, `FileUpload` does
   * not compute its own status — the parent must derive it from the upload result and pass
   * it in. The `touched` flag on this object drives border-color CSS classes.
   */
  status: {
    type: Object as () => Status,
    required: false,
    default: () => ({
      valid: true,
      message: ''
    })
  },
  /** Validation metadata. `required` controls the "(optional)" label suffix. */
  validation: {
    type: Object as () => ValidationParams,
    required: false,
    default: () => ({
      required: false,
      minLength: undefined,
      maxLength: undefined,
      regex: undefined
    })
  }
})

const emit = defineEmits<{
  (event: 'update:modelValue', value: string): void
}>()

function updateValue(imgURL: string) {
  emit('update:modelValue', imgURL)
}

const _classes = computed(() => ({
  // Les classes sont gérées ici (voir bouton)
  'border-danger': !props.status.valid && props.status.touched,
  'border-success': props.status.valid && props.status.touched
}))

const FilePond = vueFilePond(FilePondPluginFileValidateType, FilePondPluginImagePreview)

const myServer = useFirebaseStorage(props.id, updateValue)
</script>

<style lang="scss" scoped>
.file-upload {
  display: flex;
  flex-direction: column;
  margin: 0.5rem;
  cursor: pointer;

  &__label {
    margin-bottom: 0.5rem;
    font-weight: bold;
    color: var(--text);
    cursor: pointer;
  }

  &__error {
    margin-top: 0.2rem;
    font-size: 0.8rem;
    color: var(--danger);
    cursor: pointer;
  }
}
</style>
